import { Suspense, lazy, useState, useCallback, useEffect } from 'react';
import { renderIcon } from '../../utils/icons';
import { Card, EmptyState, PR } from '../../components/ui';
import { Dots } from '../../components/ui/Skeletons';
import SiteProgressChart from '../../components/SiteProgressChart';
import TasksDueSoon from '../../components/TasksDueSoon';
import TeamWorkload from '../../components/TeamWorkload';
import { api } from '../../utils/api';

const PipelineAnalytics = lazy(() => import('../Analytics/PipelineAnalytics'));

const SuspenseFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Dots /></div>
);

const WIDGET_KEY = 'lp-dash-widgets';
const ORDER_KEY = 'lp-dash-widget-order';
const WIDGETS = [
  { id: 'stats', label: 'Stat Cards' },
  { id: 'financial', label: 'Financial Overview' },
  { id: 'sites', label: 'Site Cards' },
  { id: 'activity', label: 'Recent Activity' },
  { id: 'dueSoon', label: 'Due This Week' },
  { id: 'team', label: 'Team Workload' },
  { id: 'progress', label: 'Site Progress' },
  { id: 'pipeline', label: 'Pipeline Analytics' },
];
const DEFAULT_ORDER = WIDGETS.map(w => w.id);

function loadWidgetOrder() {
  try {
    const stored = JSON.parse(localStorage.getItem(ORDER_KEY));
    if (Array.isArray(stored) && stored.length === DEFAULT_ORDER.length) {
      // Validate all IDs are present
      const valid = DEFAULT_ORDER.every(id => stored.includes(id));
      if (valid) return stored;
    }
  } catch { /* ignore */ }
  return DEFAULT_ORDER;
}

function saveWidgetOrder(order) {
  localStorage.setItem(ORDER_KEY, JSON.stringify(order));
}

function FinancialOverview({ theme, isDark, mobile, nav, setSelSite, setView, setExpWS }) {
  const [data, setData] = useState(null);
  const [aged, setAged] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/accounting/summary'),
      api.get('/api/accounting/aged-payables'),
    ]).then(([s, a]) => {
      setData(s);
      setAged(a);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Card><Dots /></Card>;
  if (!data) return null;

  const t = data.totals || {};
  const sites = data.sites || [];
  const pendingPOs = sites.reduce((n, s) => n + (s.openPOs || 0), 0);
  const unpaidBills = sites.reduce((n, s) => n + (s.unpaidBills || 0), 0);
  const overdue90 = aged?.totals?.days90 || 0;
  const overdue60 = aged?.totals?.days60 || 0;
  const overBudget = sites.filter(s => Number(s.committed || 0) > Number(s.budget || 0) && Number(s.budget || 0) > 0);

  const fmtK = (v) => {
    const n = Number(v || 0);
    if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K';
    return '$' + n.toLocaleString();
  };

  const alerts = [];
  if (overdue90 > 0) alerts.push({ text: fmtK(overdue90) + ' bills 90+ days past due', color: '#EF4444', icon: 'alert-circle' });
  if (overdue60 > 0) alerts.push({ text: fmtK(overdue60) + ' bills 60+ days past due', color: '#F59E0B', icon: 'clock' });
  if (overBudget.length > 0) alerts.push({ text: overBudget.length + ' site' + (overBudget.length > 1 ? 's' : '') + ' over budget', color: '#EF4444', icon: 'trending-up' });

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Top financial stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: mobile ? 10 : 12, marginBottom: 12 }}>
        {[
          { l: 'Total Budget', v: fmtK(t.budget), c: theme.text },
          { l: 'Total Spent', v: fmtK(t.paid), c: '#4ADE80', glow: '0 0 16px rgba(80,232,144,0.15)' },
          { l: 'Outstanding AP', v: fmtK(Number(t.billed || 0) - Number(t.paid || 0)), c: '#F59E0B', glow: '0 0 16px rgba(255,176,32,0.12)' },
          { l: 'Open POs', v: fmtK(t.committed), c: '#3B82F6', glow: '0 0 16px rgba(59,130,246,0.12)' },
        ].map((s, i) => (
          <Card key={i} onClick={() => nav('accounting')} className="stat-card" style={{ padding: mobile ? '14px' : '18px 20px', cursor: 'pointer', borderLeft: '3px solid ' + s.c + '30' }}>
            <div style={{ fontSize: 10, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>{s.l}</div>
            <div style={{ fontSize: mobile ? 22 : 28, fontFamily: "'DM Sans', sans-serif", fontWeight: 400, color: s.c, lineHeight: 1.1, textShadow: s.glow || 'none' }}>{s.v}</div>
          </Card>
        ))}
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {alerts.map((a, i) => (
            <div key={i} onClick={() => nav('accounting')} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
              background: a.color + '15', border: '1px solid ' + a.color + '30',
              borderRadius: 8, fontSize: 12, color: a.color, fontWeight: 500, cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
            }}>
              {renderIcon(a.icon, 13, a.color)} {a.text}
            </div>
          ))}
        </div>
      )}

      {/* Site financial cards */}
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(4,1fr)', gap: 12 }} className="mobile-full">
        {sites.filter(s => Number(s.budget || 0) > 0 || Number(s.committed || 0) > 0).map(s => {
          const budget = Number(s.budget || 0);
          const spent = Number(s.paid || 0);
          const pctSpent = budget > 0 ? Math.round((spent / budget) * 100) : 0;
          const pctColor = pctSpent > 100 ? '#EF4444' : pctSpent > 80 ? '#F59E0B' : '#4ADE80';
          return (
            <Card key={s.id} onClick={() => {
              setSelSite(s.id); setView('sites'); setExpWS(new Set());
              window.history.pushState({ view: 'sites', siteId: s.id }, '', '/sites/' + s.id);
            }} style={{ cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, ' + pctColor + ' ' + Math.min(pctSpent, 100) + '%, ' + theme.borderLight + ' ' + Math.min(pctSpent, 100) + '%)' }} />
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 15, fontFamily: "'DM Sans', sans-serif", fontWeight: 400, color: theme.text, letterSpacing: '-0.01em', marginBottom: 2 }}>{s.name}</div>
                {s.entity_short && <span style={{ fontSize: 10, color: theme.textMuted, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', padding: '1px 6px', borderRadius: 4 }}>{s.entity_short}</span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 11, fontFamily: "'DM Sans', sans-serif" }}>
                <div>
                  <div style={{ color: theme.textMuted, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Budget</div>
                  <div style={{ color: theme.text, fontWeight: 500 }}>{fmtK(budget)}</div>
                </div>
                <div>
                  <div style={{ color: theme.textMuted, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Spent</div>
                  <div style={{ color: pctColor, fontWeight: 500 }}>{pctSpent}%</div>
                </div>
                <div>
                  <div style={{ color: theme.textMuted, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Open POs</div>
                  <div style={{ color: theme.text, fontWeight: 500 }}>{s.openPOs || 0}</div>
                </div>
                <div>
                  <div style={{ color: theme.textMuted, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Unpaid</div>
                  <div style={{ color: (s.unpaidBills || 0) > 0 ? '#F59E0B' : theme.text, fontWeight: 500 }}>{s.unpaidBills || 0} bills</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Quick links */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        {[
          { l: 'Submit Bill', tab: 'bills' },
          { l: 'Request PO', tab: 'pos' },
          { l: 'Aged Payables', tab: 'bills' },
          { l: 'Full Accounting', tab: 'overview' },
        ].map((q, i) => (
          <button key={i} onClick={() => {
            window.history.pushState({ view: 'accounting' }, '', '/accounting?tab=' + q.tab);
            window.dispatchEvent(new PopStateEvent('popstate', { state: { view: 'accounting' } }));
          }} className="action-btn" style={{
            padding: '6px 14px', background: theme.bgHover, border: '1px solid ' + theme.borderLight,
            borderRadius: 6, color: theme.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 500,
            fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em',
          }}>
            {q.l} →
          </button>
        ))}
      </div>
    </div>
  );
}

function loadWidgetConfig() {
  try { return JSON.parse(localStorage.getItem(WIDGET_KEY)) || {}; } catch { return {}; }
}

function WidgetToggle({ config, setConfig, order, setOrder, theme, isDark }) {
  const [open, setOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const toggle = (id) => {
    const next = { ...config, [id]: !isVisible(id) };
    setConfig(next);
    localStorage.setItem(WIDGET_KEY, JSON.stringify(next));
  };
  const isVisible = (id) => config[id] !== false; // default visible

  const orderedWidgets = order.map(id => WIDGETS.find(w => w.id === id)).filter(Boolean);

  const handleDrop = (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) return;
    const next = [...order];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(dropIdx, 0, moved);
    setOrder(next);
    saveWidgetOrder(next);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} className="action-btn" style={{
        padding: '8px 16px', background: theme.bgHover, border: `1px solid ${theme.border}`,
        borderRadius: 8, color: theme.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 500,
        fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {renderIcon('settings', 12)} Widgets
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 6, background: theme.bgModal,
            border: `1px solid ${theme.border}`, borderRadius: 10, padding: '8px 0', minWidth: 220,
            boxShadow: theme.shadowLg, zIndex: 51,
          }}>
            <div style={{ padding: '4px 14px 8px', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>Drag to reorder / click to toggle</div>
            {orderedWidgets.map((w, idx) => (
              <div
                key={w.id}
                draggable
                onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; }}
                onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                onDrop={(e) => { e.preventDefault(); handleDrop(idx); setDragIdx(null); setDragOverIdx(null); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', cursor: 'grab',
                  fontSize: 13, color: isVisible(w.id) ? theme.text : theme.textMuted,
                  fontFamily: "'DM Sans', sans-serif",
                  borderLeft: dragOverIdx === idx && dragIdx !== null && dragIdx !== idx ? '3px solid #4ADE80' : '3px solid transparent',
                  opacity: dragIdx === idx ? 0.5 : 1,
                  transition: 'border-left 0.15s, opacity 0.15s',
                }}
                onMouseEnter={e => { if (dragIdx === null) e.currentTarget.style.background = theme.bgHover; }}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ color: theme.textMuted, fontSize: 11, cursor: 'grab', userSelect: 'none', lineHeight: 1 }}>&#x2807;</span>
                <div onClick={() => toggle(w.id)} style={{
                  width: 18, height: 18, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isVisible(w.id) ? theme.accentMuted : theme.bgHover,
                  border: `1px solid ${isVisible(w.id) ? theme.accent + '4D' : theme.border}`,
                  color: isVisible(w.id) ? theme.accent : 'transparent', fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', flexShrink: 0,
                }}>
                  {isVisible(w.id) ? '✓' : ''}
                </div>
                <span onClick={() => toggle(w.id)} style={{ cursor: 'pointer', flex: 1 }}>{w.label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function DashboardView({ stats, totals, sStats, overdueCount, activity, users, sites, user, mobile, nav, setSelSite, setView, setExpWS, theme, isViewer, isDark, R, N, refresh }) {
  const [widgetConfig, setWidgetConfig] = useState(loadWidgetConfig);
  const [widgetOrder, setWidgetOrder] = useState(loadWidgetOrder);
  const show = useCallback((id) => widgetConfig[id] !== false, [widgetConfig]);

  return (
    <div className="stagger-children" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 6 }}>
        <div>
          <h1 style={{ fontSize: mobile ? 28 : 36, fontFamily: "'DM Sans', sans-serif", fontWeight: 400, margin: 0, color: theme.text, lineHeight: 1.1, letterSpacing: '-0.02em' }}>Dashboard</h1>
          <p style={{ color: theme.textSecondary, margin: '4px 0 0', fontSize: 13, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.01em' }}>Welcome back, {user.name}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <WidgetToggle config={widgetConfig} setConfig={setWidgetConfig} order={widgetOrder} setOrder={setWidgetOrder} theme={theme} isDark={isDark} />
          <button onClick={() => {
            const w = window.open('', '_blank');
            const date = new Date().toLocaleDateString();
            const tot = totals.total || 0, comp = totals.complete || 0, act = totals.inProgress || 0;
            const crit = (totals.critical || 0) - (totals.critDone || 0), od = overdueCount, pct = totals.pct || 0;
            let html = '<html><head><title>Lightning Partners HQ — Dashboard Report</title><style>body{background:#fff;font-family:"Segoe UI",Arial,sans-serif;max-width:900px;margin:0 auto;padding:32px;color:#1a1a1a}h1{font-size:28px;font-weight:300;color:#1a1a1a;border-bottom:3px solid #16A34A;padding-bottom:12px;margin-bottom:4px}h2{color:#1a1a1a;margin-top:28px;font-size:18px;font-weight:600;border-bottom:1px solid #e5e7eb;padding-bottom:6px}table{width:100%;border-collapse:collapse;margin:10px 0}th{background:#16A34A;color:#fff;padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.04em}td{padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:12px}tr:nth-child(even){background:#f9fafb}.stats{display:flex;gap:16px;margin:16px 0;flex-wrap:wrap}.stat{background:#f0fdf4;border:1px solid #dcfce7;padding:14px 24px;border-radius:10px;text-align:center;min-width:110px}.stat-num{font-size:28px;font-weight:300;color:#16A34A}.stat-label{font-size:10px;color:#666;text-transform:uppercase;letter-spacing:0.08em;margin-top:2px}@media print{body{padding:0}}</style></head><body>';
            html += '<h1>Lightning Partners HQ — Dashboard Report</h1><p style="color:#666;font-size:12px">Generated: ' + date + '</p>';
            html += '<h2>Overall Stats</h2><div class="stats">';
            [['Total', tot], ['Complete', comp], ['Active', act], ['Critical', crit], ['Overdue', od], ['Progress', pct + '%']].forEach(([l, v]) => { html += '<div class="stat"><div class="stat-num">' + v + '</div><div class="stat-label">' + l + '</div></div>'; });
            html += '</div>';
            html += '<h2>Site Summary</h2><table><tr><th>Site</th><th>Items</th><th>Complete</th><th>Critical</th><th>Progress</th></tr>';
            sStats.forEach(s => { html += '<tr><td>' + s.siteName + '</td><td>' + s.total + '</td><td>' + s.complete + '</td><td>' + (s.critical - s.critDone) + '</td><td>' + s.pct + '%</td></tr>'; });
            html += '</table>';
            html += '<h2>Recent Activity</h2><table><tr><th>User</th><th>Site</th><th>Item</th><th>Change</th></tr>';
            activity.slice(0, 10).forEach(a => { html += '<tr><td>' + (a.user_name || '') + '</td><td>' + (a.site_name || '') + '</td><td>' + (a.item_doc || '') + '</td><td>' + (a.old_value || '') + ' → ' + (a.new_value || '') + '</td></tr>'; });
            html += '</table>';
            html += '<h2>Team Members</h2><table><tr><th>Name</th><th>Email</th><th>Role</th></tr>';
            users.forEach(u => { html += '<tr><td>' + u.name + '</td><td>' + (u.email || '') + '</td><td>' + (u.role || '') + '</td></tr>'; });
            html += '</table></body></html>';
            w.document.write(html); w.document.close(); w.print();
          }} className="action-btn" style={{ padding: '8px 16px', background: theme.bgHover, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em' }}>
            {renderIcon('file-text', 12)} Print Report
          </button>
        </div>
      </div>

      {/* Quick nav pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, marginTop: 16, flexWrap: 'wrap' }}>
        {[
          { l: 'Pipeline', go: 'pipeline' },
          { l: 'Accounting', go: 'accounting' },
          { l: 'Activity', go: 'activity' },
          { l: 'Calendar', go: 'calendar' },
          { l: 'Documents', go: 'documents' },
          { l: 'Partners', go: 'partners' },
        ].concat(user.role === 'admin' ? [{ l: 'Admin', go: 'admin' }] : [])
          .filter(q => !(isViewer && q.l.includes('Admin')))
          .map((q, i) => (
            <button key={i} onClick={() => nav(q.go)} className="action-btn" style={{ padding: '6px 14px', background: theme.bgHover, border: `1px solid ${theme.borderLight}`, borderRadius: 6, color: theme.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em' }}>
              {q.l}
            </button>
          ))}
      </div>

      {widgetOrder.map((id, idx) => {
        if (!show(id)) return null;

        // dueSoon and team: if adjacent in order, render as a combined 2-col row on the first one
        if (id === 'dueSoon' || id === 'team') {
          const otherId = id === 'dueSoon' ? 'team' : 'dueSoon';
          const otherIdx = widgetOrder.indexOf(otherId);
          const adjacent = Math.abs(otherIdx - idx) === 1;
          const isFirst = idx < otherIdx;

          // If adjacent and this is the second one, skip (already rendered by the first)
          if (adjacent && !isFirst) return null;

          if (adjacent && show(otherId)) {
            // Render combined 2-col row
            const dueFirst = id === 'dueSoon';
            return (
              <div key="dueSoon-team" style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 12, marginTop: idx > 0 ? 12 : 0 }}>
                {dueFirst ? <TasksDueSoon /> : <TeamWorkload users={users} />}
                {dueFirst ? <TeamWorkload users={users} /> : <TasksDueSoon />}
              </div>
            );
          }
          // Not adjacent or other is hidden — render standalone
          return (
            <div key={id} style={{ marginTop: idx > 0 ? 12 : 0 }}>
              {id === 'dueSoon' ? <TasksDueSoon /> : <TeamWorkload users={users} />}
            </div>
          );
        }

        if (id === 'stats') return (
          <div key="stats" style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: mobile ? 10 : 12, marginBottom: 24 }}>
            {[
              { l: 'Total', v: stats ? totals.total || 0 : '—', c: theme.text, go: 'sites' },
              { l: 'Complete', v: stats ? totals.complete || 0 : '—', c: '#4ADE80', glow: '0 0 16px rgba(80,232,144,0.15)', go: 'sites' },
              { l: 'Active', v: stats ? totals.inProgress || 0 : '—', c: '#4ADE80', go: 'sites' },
              { l: 'Critical', v: stats ? (totals.critical || 0) - (totals.critDone || 0) : '—', c: '#EF4444', glow: '0 0 16px rgba(255,85,85,0.15)', go: 'sites' },
              { l: 'Overdue', v: stats ? overdueCount : '—', c: '#F59E0B', glow: '0 0 16px rgba(255,176,32,0.12)', go: 'sites' },
              { l: 'Progress', v: stats ? (totals.pct || 0) + '%' : '—', c: '#4ADE80', glow: '0 0 16px rgba(80,232,144,0.12)', go: 'sites' },
            ].map((s, i) => (
              <Card key={i} onClick={() => nav(s.go)} className="stat-card" style={{ padding: mobile ? '14px' : '18px 20px', cursor: 'pointer', borderLeft: `3px solid ${s.c}30` }}>
                <div style={{ fontSize: 10, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>{s.l}</div>
                <div style={{ fontSize: mobile ? 26 : 32, fontFamily: "'DM Sans', sans-serif", fontWeight: 400, color: s.c, lineHeight: 1.1, textShadow: s.glow || 'none' }}>{s.v}</div>
              </Card>
            ))}
          </div>
        );

        if (id === 'financial') return (
          <FinancialOverview key="financial" theme={theme} isDark={isDark} mobile={mobile} nav={nav} setSelSite={setSelSite} setView={setView} setExpWS={setExpWS} />
        );

        if (id === 'sites') return (
          <div key="sites" style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(4,1fr)', gap: 12, marginBottom: 24 }} className="mobile-full">
            {[...sStats].sort((a, b) => (a.siteId || 0) - (b.siteId || 0)).map((s, i) => (
              <Card key={s.siteId || i} onClick={() => {
                setSelSite(s.siteId); setView('sites'); setExpWS(new Set());
                window.history.pushState({ view: 'sites', siteId: s.siteId }, '', '/sites/' + s.siteId);
              }} style={{ cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: s.pct >= 100 ? '#4ADE80' : `linear-gradient(90deg, ${theme.textSecondary} ${s.pct}%, ${theme.borderLight} ${s.pct}%)`, boxShadow: s.pct >= 100 ? '0 0 8px rgba(80,232,144,0.3)' : 'none' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 16, fontFamily: "'DM Sans', sans-serif", fontWeight: 400, color: theme.text, display: 'flex', alignItems: 'center', letterSpacing: '-0.01em' }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: s.pct >= 100 ? '#4ADE80' : s.pct > 0 ? '#F59E0B' : theme.textMuted, marginRight: 8, flexShrink: 0, boxShadow: s.pct >= 100 ? '0 0 6px rgba(80,232,144,0.5)' : s.pct > 0 ? '0 0 6px rgba(255,176,32,0.4)' : 'none' }} />
                      {s.siteName}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: theme.textSecondary, fontFamily: "'DM Sans', sans-serif", marginTop: 4 }}>
                      <span>{s.total} items</span>
                      {(s.critical - s.critDone) > 0 && <span style={{ background: 'rgba(255,85,85,0.15)', color: '#EF4444', padding: '1px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600, boxShadow: '0 0 6px rgba(255,85,85,0.12)' }}>{s.critical - s.critDone} critical</span>}
                    </div>
                  </div>
                  <PR pct={s.pct} sz={42} sk={3} />
                </div>
              </Card>
            ))}
          </div>
        );

        if (id === 'activity') return (
          <Card key="activity">
            <h3 style={{ fontSize: 20, fontFamily: "'DM Sans', sans-serif", fontWeight: 400, color: theme.text, margin: '0 0 14px', letterSpacing: '-0.01em' }}>Recent Activity</h3>
            {activity.length === 0 && <EmptyState icon="clipboard-list" title="No activity yet" description="Activity will appear here as your team works" action="View Activity" onAction={() => nav('activity')} />}
            {activity.slice(0, 6).map((a, i) => (
              <div key={i} style={{ padding: '7px 0', borderBottom: i < 5 ? `1px solid ${theme.borderLight}` : 'none', fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
                <span style={{ color: theme.text, fontWeight: 500 }}>{a.user_name}</span>
                {a.site_name && <span style={{ color: theme.textMuted, fontSize: 12 }}> [{a.site_name}]</span>}
                <span style={{ color: theme.textMuted }}> — </span>
                <span style={{ color: theme.text }}>{a.item_doc}</span>
                <span style={{ color: theme.textMuted }}> : </span>
                <span style={{ color: '#EF4444', opacity: 0.8 }}>{a.old_value}</span>
                <span style={{ color: theme.textMuted }}> → </span>
                <span style={{ color: theme.accent, fontWeight: 500 }}>{a.new_value}</span>
              </div>
            ))}
            {activity.length > 0 && <button onClick={() => nav('activity')} className="action-btn" style={{ background: 'none', border: 'none', color: theme.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 500, marginTop: 12, padding: 0, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em' }}>View all activity →</button>}
          </Card>
        );

        if (id === 'progress') return (
          <div key="progress" style={{ marginTop: 12 }}><SiteProgressChart siteStats={sStats} /></div>
        );

        if (id === 'pipeline') return (
          <Suspense key="pipeline" fallback={<SuspenseFallback />}><PipelineAnalytics /></Suspense>
        );

        return null;
      })}
    </div>
  );
}
